// GlanceZonePage - one zone of the building in full (guide section 13):
// summary cards, the zone exploded by height band, a plan with 20 ft of
// context, and every item the drawings list there, grouped by discipline.
//
// Opened in a new tab from the At a Glance stack. The address carries the
// view - #level=L1&zone=3&sys=telecom,power&two=1 - and is rewritten on
// every change, so it is always a shareable link to exactly what is on
// screen; the page keeps its own level, zone and system controls, so it
// still works when a host drops the hash.
import React, { useEffect, useMemo, useState } from "react";
import { filterPeopleOption, renderPeopleOption } from "components/PeoplePicker/PeoplePicker";
import { peoplePickerOptions } from "components/PeoplePicker/peoplePickerOptions";
import { Link, useHistory, useParams } from "react-router-dom";
import { Button, Input, Modal, Select, Skeleton, message } from "antd";
import { url, tokenRef } from "api";
import ZoneBands from "components/Coordination/Glance/ZoneBands";
import SystemChips from "components/Coordination/Glance/SystemChips";
import GlanceThemeToggle, { useGlanceTheme } from "components/Coordination/Glance/GlanceThemeToggle";
import {
  CATEGORY_FILL, ROOF, bounds, bucketKey, dotsPath, drawnSystems, hasRoofLevel, isCovered, levelLabel, linesPath, marksPath,
  carriesSize, openNewView, orthoPlan, parseZoneHash, zoneCeilings, pointsAttr, polysPath, sizeMatch, sortedLevels, systemColor, zoneHash,
} from "components/Coordination/Glance/glanceModel";
import "components/Coordination/Glance/glance.css";
import { useRememberRouteProject } from "helpers/projectSelection";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const DISCIPLINES = { telecom: "Telecom", electrical: "Electrical", fire: "Fire protection", mechanical: "Mechanical", plumbing: "Plumbing" };
const CONTEXT_FT = 20;

// 12.5 ft -> 12'-6" (to the nearest inch).
export const ftIn = (d) => { const inches = Math.round(Math.abs(d) * 12); return `${Math.floor(inches / 12)}'-${inches % 12}"`; };

function ZonePlan({ manifest, level, zone, active, items, size = null, tool = null, measure = [], markups = [], onPick }) {
  // Tags of the size a view asked for stand out; with no view, none do.
  const hi = (i) => !!size && sizeMatch(i, size);
  const z = manifest.zones[zone];
  const b = bounds(manifest.levels || []);
  const isRoof = level === ROOF;
  const x0 = Math.max(b.xmin - 4, z.x0 - CONTEXT_FT); const x1 = Math.min(b.xmax + 4, z.x1 + CONTEXT_FT);
  const ymin = b.ymin - 4; const ymax = b.ymax + 4;
  const W = 1280; const sc = Math.min((W - 80) / (x1 - x0), 12);
  const { P, inverse, height: H } = orthoPlan({ xmin: x0, ymin, h: ymax - ymin }, { scale: sc, left: 40, bottom: 30, top: 30 });
  const lvl = isRoof
    ? { rooms: [], footprint: (sortedLevels(manifest).slice(-1)[0] || {}).footprint }
    : (manifest.levels || []).find((l) => l.id === level) || { rooms: [] };
  const systems = drawnSystems(manifest).filter((s) => active.has(s.id) && (isRoof ? s.id === ROOF : s.id !== ROOF));
  const zx0 = Math.max(z.x0, x0); const zx1 = Math.min(z.x1, x1);
  const neighbours = (manifest.zones || []).filter((q) => q.x1 > x0 && q.x0 < x1).map((q) => q.index);
  const clipId = `zc-${level}-${zone}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Plan of ${levelLabel(manifest, level)}, ${z.label}`}
         style={tool ? { cursor: "crosshair" } : undefined}
         onClick={tool && onPick ? (e) => {
           // Screen -> building feet: the inverse of P.
           const svg = e.currentTarget; const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
           const q = pt.matrixTransform(svg.getScreenCTM().inverse());
           onPick(inverse(q.x, q.y));
         } : undefined}>
      <defs><clipPath id={clipId}><rect x={40} y={30} width={(x1 - x0) * sc} height={(ymax - ymin) * sc} /></clipPath></defs>
      <g clipPath={`url(#${clipId})`}>
        {lvl.footprint && <polygon points={pointsAttr(lvl.footprint, (p) => P(p[0], p[1]))} fill={isRoof ? "var(--roof)" : "var(--sheet)"} stroke="var(--ink)" strokeWidth="1.5" />}
        {lvl.rooms.map((r) => (
          <polygon key={r.id} points={pointsAttr(r.polygon, (p) => P(p[0], p[1]))} fill={CATEGORY_FILL[r.category] || "var(--circ)"} fillOpacity="0.55" stroke="var(--line)" strokeWidth="0.6">
            <title>{[r.name, r.number].filter(Boolean).join(" ")}</title>
          </polygon>
        ))}
        {systems.map((s) => neighbours.map((zi) => {
          const bucket = (manifest.buckets || {})[bucketKey(level, s.id, zi)];
          if (!bucket) return null;
          const c = systemColor(s);
          return (
            <g key={`${s.id}-${zi}`} style={{ pointerEvents: "none" }}>
              {bucket.lines && <path d={linesPath(bucket.lines, P)} stroke={c} strokeWidth={s.id === "ducts" ? 1 : 1.4} fill="none" strokeDasharray={s.dashed ? "5 3" : undefined} />}
              {bucket.polys && <path d={polysPath(bucket.polys, P)} fill={c} stroke="var(--systemink)" strokeWidth="0.6" />}
              {bucket.dots && <path d={dotsPath(bucket.dots, P, 2.2)} fill={c} />}
              {bucket.marks && <path d={marksPath(bucket.marks, P, 6)} fill={c} stroke="var(--sheet)" />}
            </g>
          );
        }))}
        {lvl.rooms.filter((r) => (r.areaSf || 0) > 40).map((r) => {
          let cx = 0; let cy = 0; r.polygon.forEach((q) => { cx += q[0]; cy += q[1]; }); cx /= r.polygon.length; cy /= r.polygon.length;
          if (cx < zx0 || cx >= zx1) return null;
          const p = P(cx, cy);
          return <text key={r.id} x={p[0]} y={p[1]} textAnchor="middle" fontSize="12" fill="var(--ink2)" style={{ pointerEvents: "none" }}>{[r.name, r.number].filter(Boolean).join(" ")}</text>;
        })}
        {items.filter((i) => i.c === "Keynoted pathways").map((i, n) => {
          const p = P(i.x, i.y);
          return (
            <g key={`kn-${n}`}>
              <circle cx={p[0]} cy={p[1]} r="9" fill={hi(i) ? "var(--riser)" : "var(--sheet)"} stroke="var(--ink)" />
              <text x={p[0]} y={p[1] + 4} textAnchor="middle" fontSize="11" fill={hi(i) ? "var(--sheet)" : "var(--ink)"}>{String(i.tag).replace(/^KN\s*/i, "")}</text>
              <title>{i.t}</title>
            </g>
          );
        })}
        {items.filter((i) => i.c === "Equipment tags" || i.c === "Roof equipment").map((i, n) => {
          const p = P(i.x, i.y);
          return <text key={`eq-${n}`} x={p[0]} y={p[1]} fontSize="12" fill="var(--ink)" paintOrder="stroke" stroke="var(--sheet)" strokeWidth="3">{i.tag || i.t}</text>;
        })}
      </g>
      {/* Context outside the zone stays visible but dimmed. */}
      {zx0 > x0 && <rect x={40} y={30} width={(zx0 - x0) * sc} height={(ymax - ymin) * sc} fill="var(--paper)" fillOpacity="0.7" />}
      {zx1 < x1 && <rect x={40 + (zx1 - x0) * sc} y={30} width={(x1 - zx1) * sc} height={(ymax - ymin) * sc} fill="var(--paper)" fillOpacity="0.7" />}
      <rect x={40 + (zx0 - x0) * sc} y={30} width={(zx1 - zx0) * sc} height={(ymax - ymin) * sc} fill="none" stroke="var(--ink)" strokeWidth="1.5" strokeDasharray="6 4" />
      <text x={40 + (zx0 - x0) * sc + 6} y={22} fontSize="14" fontWeight="600" fill="var(--ink)">{z.label}</text>
      {/* Measure and mark (guide 20.4): a dimension in feet and inches, and the notes on this level. */}
      {measure.length > 0 && (() => {
        const [a, b2] = [P(measure[0][0], measure[0][1]), measure[1] ? P(measure[1][0], measure[1][1]) : null];
        const d = measure[1] ? Math.hypot(measure[1][0] - measure[0][0], measure[1][1] - measure[0][1]) : null;
        return (
          <g className="g-measure" style={{ pointerEvents: "none" }}>
            <circle cx={a[0]} cy={a[1]} r="4" fill="var(--riser)" />
            {b2 && <line x1={a[0]} y1={a[1]} x2={b2[0]} y2={b2[1]} stroke="var(--riser)" strokeWidth="2" />}
            {b2 && <circle cx={b2[0]} cy={b2[1]} r="4" fill="var(--riser)" />}
            {b2 && <text x={(a[0] + b2[0]) / 2} y={(a[1] + b2[1]) / 2 - 8} textAnchor="middle" fontSize="15" fontWeight="600" fill="var(--riser)" paintOrder="stroke" stroke="var(--sheet)" strokeWidth="4">{ftIn(d)}</text>}
          </g>
        );
      })()}
      {markups.map((m, n) => {
        const p = P(m.at[0], m.at[1]);
        return (
          <g key={m.id} style={{ pointerEvents: "none" }}>
            <path d={`M${p[0]},${p[1]} l-7,-14 a8,8 0 1 1 14,0 z`} fill={m.issueId ? "var(--occ)" : "var(--riser)"} stroke="var(--sheet)" />
            <text x={p[0]} y={p[1] - 16} textAnchor="middle" fontSize="10" fill="var(--sheet)">{n + 1}</text>
          </g>
        );
      })}
    </svg>
  );
}

// Guide 20.4, "Measure and mark": a distance in feet and inches (drawn here,
// never stored), and notes marked on the plan that become issues when they
// are assigned (routes/projectGlance /markups).
function usePlanTools(projectId, level, canEdit) {
  const [tool, setTool] = useState(null);           // null | "measure" | "mark"
  const [measure, setMeasure] = useState([]);
  const [markups, setMarkups] = useState([]);
  const [draft, setDraft] = useState(null);         // { at, text } while a note is being written
  const api = (path, opts = {}) => fetch(`${url}/projects/${projectId}/glance${path}`, {
    ...opts, headers: { ...(opts.body ? { "Content-Type": "application/json" } : {}), ...auth() },
  }).then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`); return b; });
  const load = () => (level ? api(`/markups?level=${encodeURIComponent(level)}`).then((o) => setMarkups(o.markups || [])).catch(() => setMarkups([])) : Promise.resolve(setMarkups([])));
  useEffect(() => { setMeasure([]); load(); }, [projectId, level]); // eslint-disable-line react-hooks/exhaustive-deps
  const onPick = (p) => {
    if (tool === "measure") setMeasure((m) => (m.length >= 2 ? [p] : [...m, p]));
    else if (tool === "mark" && canEdit) setDraft({ at: p, text: "" });
  };
  return { tool, setTool, measure, setMeasure, markups, draft, setDraft, onPick, load, api };
}

function PlanToolbar({ t, canEdit }) {
  const d = t.measure.length === 2 ? Math.hypot(t.measure[1][0] - t.measure[0][0], t.measure[1][1] - t.measure[0][1]) : null;
  return (
    <div className="g-seg g-noprint" role="group" aria-label="Plan tools" style={{ marginBottom: 8 }}>
      <button type="button" aria-pressed={t.tool === "measure"} onClick={() => { t.setTool(t.tool === "measure" ? null : "measure"); t.setMeasure([]); }}>Measure</button>
      {canEdit && <button type="button" aria-pressed={t.tool === "mark"} onClick={() => t.setTool(t.tool === "mark" ? null : "mark")}>Mark a note</button>}
      <span className="g-tsub" role="status" aria-live="polite" style={{ marginLeft: 10 }}>
        {t.tool === "measure" ? (d != null ? `Distance ${ftIn(d)} (${d.toFixed(1)} ft). Click to start again.` : t.measure.length ? "Click the second point" : "Click the first point") : ""}
        {t.tool === "mark" ? "Click where the note goes" : ""}
      </span>
    </div>
  );
}

// Notes are technicalDocsAdd to mark and assign, technicalDocsDelete to
// remove (the server's can* flags, 2026-10-14). A partner marks notes and
// removes their own company's when the owner's Coordination access switches
// say so (canAddNote / canDeleteOwnNote, 2026-10-15) - never Assign, which
// makes an issue on the owner's project.
function PlanNotes({ t, canAdd, canDelete, canRemoveOwn, projectId }) {
  const [assigning, setAssigning] = useState(null);   // markup id
  const [roster, setRoster] = useState(null);
  const [userId, setUserId] = useState(undefined);
  const openAssign = (m) => {
    setAssigning(m.id); setUserId(undefined);
    if (!roster) fetch(`${url}/projects/${projectId}/participants/roster`, { headers: auth() }).then((r) => r.json()).then((g) => setRoster(Array.isArray(g) ? g : [])).catch(() => setRoster([]));
  };
  const makeIssue = async () => {
    try {
      const out = await t.api(`/markups/${assigning}/issue`, { method: "POST", body: JSON.stringify({ userId }) });
      message.success(`Issue ${out.label || ""} made from the note`.replace("  ", " "));
      setAssigning(null); t.load();
    } catch (e) { message.error(e.message); }
  };
  if (!t.markups.length) return null;
  // The shared picker's options (components/PeoplePicker, owner 2026-10-15):
  // partners under their trade, people tagged by kind. This route assigns an
  // ACCOUNT (userId), so a roster contact with no account - and a partner
  // executive, a reader - is listed greyed out with the reason rather than
  // left off the list.
  const people = peoplePickerOptions(roster, { personValue: (p) => p.userId, noAccount: "disable", executives: "disable" });
  return (
    <ol className="g-notes-list">
      {t.markups.map((m) => (
        <li key={m.id}>
          {m.text} <span className="g-tsub">· {m.createdByName}</span>
          {m.issueId ? <span className="g-tsub"> · issue {m.issueLabel || m.issueId}</span> : (canAdd || canDelete || canRemoveOwn) && (
            assigning === m.id ? (
              <span className="g-actions">
                <Select size="small" showSearch allowClear style={{ width: 240 }} placeholder="Assign to" value={userId} onChange={setUserId}
                        loading={!roster} filterOption={filterPeopleOption} optionRender={renderPeopleOption}
                        popupMatchSelectWidth={false} options={people} aria-label="Assign the issue to" />
                <Button size="small" type="primary" onClick={makeIssue}>Make it an issue</Button>
                <Button size="small" type="text" onClick={() => setAssigning(null)}>Cancel</Button>
              </span>
            ) : (
              <>
                {" "}{canAdd && <button type="button" className="g-linkbtn" onClick={() => openAssign(m)}>Assign as an issue</button>}
                {(canDelete || canRemoveOwn) && <button type="button" className="g-linkbtn" onClick={() => t.api(`/markups/${m.id}`, { method: "DELETE" }).then(t.load).catch((e) => message.error(e.message))}>Remove</button>}
              </>
            )
          )}
        </li>
      ))}
    </ol>
  );
}

export default function GlanceZonePage() {
  const { projectId } = useParams();
  const history = useHistory();
  useGlanceTheme();
  useRememberRouteProject(projectId);
  const [glance, setGlance] = useState(null);
  const [error, setError] = useState("");
  const [state, setState] = useState(() => parseZoneHash(typeof window !== "undefined" ? window.location.hash : ""));
  const [zoneData, setZoneData] = useState(null);

  useEffect(() => {
    const font = "https://fonts.googleapis.com/css2?family=Barlow+Semi+Condensed:wght@400;500;600&display=swap";
    if (!document.querySelector(`link[href="${font}"]`)) {
      const link = document.createElement("link"); link.rel = "stylesheet"; link.href = font; document.head.appendChild(link);
    }
    fetch(`${url}/projects/${projectId}/glance`, { headers: auth() })
      .then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`); return b; })
      .then(setGlance).catch((e) => setError(e.message));
  }, [projectId]);

  useEffect(() => {
    const onHash = () => setState(parseZoneHash(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const manifest = glance && glance.manifest;
  const drawn = useMemo(() => drawnSystems(manifest), [manifest]);
  const levels = useMemo(() => sortedLevels(manifest), [manifest]);
  const levelIds = useMemo(() => [...levels.map((l) => l.id), ...(hasRoofLevel(manifest) ? [ROOF] : [])], [levels, manifest]);
  const zones = (manifest && manifest.zones) || [];
  const level = levelIds.includes(state.level) ? state.level : levelIds[0];
  const zone = state.zone != null && state.zone < zones.length ? state.zone : 0;
  const active = useMemo(() => new Set(state.sys ? state.sys.filter((id) => drawn.some((s) => s.id === id)) : drawn.map((s) => s.id)), [state.sys, drawn]);
  // A size comes only from a technical view (parseZoneHash): no view, no size filter.
  const size = state.size || null;
  const only = !!(size && state.only);
  const hi = (i) => !!size && sizeMatch(i, size);

  // Keep the address in step with the screen.
  useEffect(() => {
    if (!manifest || !level) return;
    const next = zoneHash({ level, zone, sys: [...active], size, view: state.view, only });
    if (window.location.hash !== next) window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${next}`);
  }, [manifest, level, zone, active, size, state.view, only]);

  useEffect(() => {
    if (!manifest || !level || !zones.length) return;
    setZoneData(null);
    fetch(`${url}/projects/${projectId}/glance/zone?level=${encodeURIComponent(level)}&zone=${zone}`, { headers: auth() })
      .then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`); return b; })
      .then(setZoneData).catch((e) => setError(e.message));
  }, [projectId, manifest, level, zone, zones.length]);

  const set = (patch) => setState((s) => ({ ...s, level, zone, sys: [...active], ...patch }));
  const plan = usePlanTools(projectId, level || "", !!(glance && (glance.canAdd || glance.canAddNote)));

  if (error && !glance) return <div className="bl-glance"><div className="g-empty">{error}</div></div>;
  if (!manifest) return <Skeleton active paragraph={{ rows: 12 }} />;
  if (!zones.length || !drawn.length) {
    return (
      <div className="bl-glance">
        <div className="g-empty">
          <b>This project's overview has no zones yet.</b> Zones and systems appear once building data with grid zones is published on the At a Glance tab.
          {" "}<Link to={`/coordination/${projectId}?tab=glance`}>Back to the overview</Link>
        </div>
      </div>
    );
  }

  const z = zones[zone];
  const keep = (i) => (!i.s || active.has(i.s)) && (!only || !carriesSize(i) || sizeMatch(i, size));
  const items = zoneData ? zoneData.items.filter(keep) : [];
  const groups = zoneData ? zoneData.groups.filter(keep) : [];
  const systemsHere = drawn.filter((s) => active.has(s.id) && (level === ROOF ? s.id === ROOF : s.id !== ROOF));
  const notReceived = systemsHere.filter((s) => !isCovered(s, level, zone));
  const keynotes = items.filter((i) => i.c === "Keynoted pathways");
  const cards = [
    ...(keynotes.length ? [{ label: "Telecom keynote tags", n: keynotes.length, sub: size ? `${keynotes.filter(hi).length} include ${size}` : "Tags on the telecom plans" }] : []),
    ...systemsHere.map((s) => {
      const rows = items.filter((i) => i.s === s.id && i.c !== "Keynoted pathways");
      const cats = {};
      rows.forEach((i) => { cats[i.c] = (cats[i.c] || 0) + 1; });
      const top = Object.entries(cats).sort((a, c) => c[1] - a[1]).slice(0, 2).map(([c]) => c).join(", ");
      return { label: s.label, n: rows.length, sub: !isCovered(s, level, zone) ? "Not in received drawings" : top || "None drawn", color: systemColor(s) };
    }),
  ];
  const byDiscipline = {};
  groups.forEach((g) => { (byDiscipline[g.d] = byDiscipline[g.d] || []).push(g); });

  return (
    <div className="bl-glance bl-glance-zone">
      <p className="g-sub" style={{ margin: 0 }}>
        <Link to={`/coordination/${projectId}?tab=glance`}>{manifest.title || "Project"}</Link>, zone detail <GlanceThemeToggle />
      </p>
      <h1 className="g-title">{levelLabel(manifest, level)}, {z.label}</h1>
      <p className="g-sub">
        {notReceived.length
          ? `${notReceived.map((s) => `${s.label} (${(s.coverage && s.coverage.note) || "part of the building"})`).join("; ")}: not in the received drawings for this zone. `
          : "Every system shown is covered by the received drawings here. "}
        {(() => { const c = zoneCeilings((manifest.levels || []).find((l) => l.id === level), z); return c ? `Ceilings here: ${c}, from the ceiling plan. ` : ""; })()}
        The address of this page is a link to exactly this view.
        {glance.canAdd && (
          // Guide 20.3: a zone (hover panel or this page, and its plan) is a
          // short route to a view of that level, zone and the systems shown.
          <> <button type="button" className="g-linkbtn" onClick={() => {
            const shown = drawn.filter((s) => active.has(s.id));
            openNewView(history, projectId, {
              title: `${levelLabel(manifest, level)} ${z.label}, ${shown.length === drawn.length ? "all systems" : shown.map((s) => s.label.toLowerCase()).join(" versus ")}`,
              request: { levels: [level], zones: [zone], systems: shown.map((s) => s.id), context: ["pathways", "rooms"], include: ["iso", "plan", "schedule"] },
            });
          }}>Make a technical view of this zone</button></>
        )}
      </p>

      <div className="g-bar">
        <div className="g-seg" role="group" aria-label="Level">
          {levelIds.map((id) => <button key={id} type="button" aria-pressed={id === level} onClick={() => set({ level: id })}>{levelLabel(manifest, id)}</button>)}
        </div>
        <div className="g-seg" role="group" aria-label="Zone">
          {zones.map((q) => <button key={q.index} type="button" aria-pressed={q.index === zone} onClick={() => set({ zone: q.index })}>{q.label.replace(/^grids /, "")}</button>)}
        </div>
        {size ? (
          <label className="g-check" title="From the technical view this page was opened from">
            <input type="checkbox" checked={only} onChange={(e) => set({ only: e.target.checked })} /> {size} items only ({state.view || "from a view"})
          </label>
        ) : null}
        <SystemChips manifest={manifest} active={active} onChange={(next) => set({ sys: [...next] })} compact />
      </div>

      <div className="g-cards">
        {cards.map((c) => (
          <div key={c.label}><b>{c.n.toLocaleString()}</b>{c.color && <i className="g-dot" style={{ background: c.color }} />}{c.label}<br /><span>{c.sub}</span></div>
        ))}
      </div>

      <section>
        <h2>Exploded view</h2>
        <p className="g-note">
          Each band is lifted by height so pathways, fixtures, ducts and pipes separate. {systemsHere.filter((s) => s.heightBasis === "schematic").length
            ? `${systemsHere.filter((s) => s.heightBasis === "schematic").map((s) => s.label).join(", ")}: heights are schematic, the 2D drawings do not give them.`
            : ""}
        </p>
        <div className="g-svgwrap">
          <ZoneBands manifest={manifest} level={level} zone={zone} active={active} items={items} detailed width={1280} />
        </div>
      </section>

      <section>
        <h2>Plan view</h2>
        <p className="g-note">Top-down view of the zone with {CONTEXT_FT} ft of context on each side, dimmed.</p>
        <PlanToolbar t={plan} canEdit={!!(glance.canAdd || glance.canAddNote)} />
        <div className="g-svgwrap"><ZonePlan manifest={manifest} level={level} zone={zone} active={active} items={items} size={size}
                                             tool={plan.tool} measure={plan.measure} markups={plan.markups} onPick={plan.onPick} /></div>
        <PlanNotes t={plan} canAdd={!!glance.canAdd} canDelete={!!glance.canDelete} canRemoveOwn={!!glance.canDeleteOwnNote} projectId={projectId} />
        <Modal open={!!plan.draft} title="A note on the plan" okText="Save the note" onCancel={() => plan.setDraft(null)}
               onOk={async () => {
                 try {
                   await plan.api("/markups", { method: "POST", body: JSON.stringify({ level, zone, at: plan.draft.at, text: plan.draft.text }) });
                   plan.setDraft(null); plan.load();
                 } catch (e) { message.error(e.message); }
               }}>
          <p className="g-tsub">Saved on this level for everyone on the project. Assign it to someone to make it an issue.</p>
          <Input.TextArea id="plan-note" autoFocus rows={3} maxLength={500} value={plan.draft ? plan.draft.text : ""} aria-label="The note"
                          onChange={(e) => plan.setDraft({ ...plan.draft, text: e.target.value })} />
        </Modal>
      </section>

      <section>
        <h2>Everything in this zone</h2>
        <p className="g-note">Items read from the drawings, grouped by discipline and filtered by the systems above. Counts come from plan symbols and tags, not from schedules.</p>
        {!zoneData && <Skeleton active paragraph={{ rows: 6 }} />}
        {zoneData && !groups.length && <p className="g-note">Nothing drawn in this zone for the systems selected.</p>}
        {Object.entries(byDiscipline).map(([d, rows]) => {
          const s = drawn.find((x) => x.id === rows[0].s);
          return (
            <div key={d}>
              <h3><i className="g-dot" style={{ background: s ? systemColor(s) : "var(--ink2)" }} />{DISCIPLINES[d] || d}</h3>
              <div className="g-tablewrap g-tall">
                <table>
                  <thead><tr><th>Category</th><th>Type</th><th>Tag</th><th className="g-num">Count</th><th>Rooms</th><th>Source</th></tr></thead>
                  <tbody>
                    {rows.map((g) => (
                      <tr key={`${g.c}|${g.t}|${g.tag}`}>
                        <td title={g.cn && g.cn !== g.c ? `Layer ${g.c}` : undefined}>{g.cn || g.c}</td>
                        <td>{g.t}{hi(g) && <span className="g-pill" style={{ color: "var(--riser)", marginLeft: 6 }}>{size}</span>}</td>
                        <td>{g.tag}</td>
                        <td className="g-num">{g.n}</td>
                        <td>{g.rooms.slice(0, 4).join(", ")}{g.rooms.length > 4 ? ` and ${g.rooms.length - 4} more` : ""}</td>
                        <td>{g.src.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        <div className="g-callout">
          <b>Schedules are separate inputs.</b> Panel schedules, the fixture schedule and the equipment schedules add circuits, loads,
          and airflow to these rows once they are uploaded; the import checklist on the overview shows which have arrived.
        </div>
      </section>
    </div>
  );
}
